import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '~components/tooltip';
import Typography from '~components/typography';
import { TRANSACTION_CATEGORY_OPTIONS } from '~graphql-types';
import {
  BasicCommercialFilled,
  BasicDiscountFilled,
  BasicHome,
  BasicInstitutionFilled,
  BasicOther,
  BasicServicesFoodFilled,
  BasicShoppingCartFilled,
  NavMaintenance,
  SettingsDeposit,
} from '~static/svg-components';
import { toThousands } from '~utils/to-thousands';
import PaymentItemStatusIcon from '../booking/booking-details/payment-item-status-icon';

const useStyles = makeStyles(
  (theme) => ({
    root: {
      ...theme.flex.crossStart,
      minWidth: '360px',
      padding: '10px 12px 12px 16px',
      minHeight: '70px',
      marginBottom: '12px',
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.small,
      border: `1px solid ${theme.colors.greyE8}`,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.colors.greyFa,
      },
    },

    name: {
      ...theme.flex.crossCenter,
      wordBreak: 'break-all',
    },

    categoryIcon: {
      ...theme.flex.center,
      width: 20,
      height: 20,
      marginLeft: 8,
      backgroundColor: theme.colors.greyE8,
      borderRadius: theme.borderRadius.full,
      '& svg': {
        width: 12,
        height: 12,
        fill: theme.colors.grey66,
      },
    },

    nameReceipt: {
      flex: 1,
    },

    receiptNumber: {
      marginTop: 4,
    },

    totalAmount: {
      ...theme.flex.center,
    },
  }),
  { name: 'TransactionRecord' }
);

function PaymentItem({ payment }) {
  const { t } = useTranslation(['graphql']);
  const classes = useStyles();

  const {
    name,
    category,
    receiptNumber,
    status,
    totalAmount,
    description,
    propertyId,
    bookingId,
    currency,
  } = payment;

  const paymentsCategoryIcon = () => {
    switch (category) {
      case TRANSACTION_CATEGORY_OPTIONS.ADMINISTRATION_FEE:
        return <SettingsDeposit />;
      case TRANSACTION_CATEGORY_OPTIONS.COMMERCIAL:
        return <BasicCommercialFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.CONSUMABLE:
        return <BasicShoppingCartFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.DAMAGE:
        return <NavMaintenance />;
      case TRANSACTION_CATEGORY_OPTIONS.DISCOUNT:
        return <BasicDiscountFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.NOMINATION:
        return <BasicInstitutionFilled />;
      case TRANSACTION_CATEGORY_OPTIONS.OTHER:
        return <BasicOther />;
      case TRANSACTION_CATEGORY_OPTIONS.RENTAL:
        return <BasicHome />;
      case TRANSACTION_CATEGORY_OPTIONS.SERVICE:
        return <BasicServicesFoodFilled />;
      case null:
        return <BasicOther />;

      default:
        break;
    }
  };

  const renderName = () => (
    <Typography variant="body1" themeColor="dark" className={classes.name}>
      {`${name || '-'}${description ? ` -  ${description}` : ''}`}
      <Tooltip
        title={t(`graphql:sales_item_category_option.${category || 'OTHER'}`)}
        placement="top"
      >
        <span className={classes.categoryIcon}>{paymentsCategoryIcon()}</span>
      </Tooltip>
    </Typography>
  );

  const renderReceipt = () => (
    <Typography variant="body2" themeColor="grey66" className={classes.receiptNumber}>
      {receiptNumber}
    </Typography>
  );

  const renderAmount = () => (
    <div className={classes.totalAmount}>
      <Typography variant="h5" themeColor="dark" style={{ marginRight: 8 }}>
        {currency} {toThousands(totalAmount)}
      </Typography>
      <PaymentItemStatusIcon status={status} />
    </div>
  );

  const openBookingDetailPage = () => {
    const bookingDetailsUrl = queryString.stringifyUrl({
      url: '/booking/booking-details',
      query: { propertyId, bookingId },
    });
    window.open(bookingDetailsUrl);
  };

  return (
    <div
      className={classes.root}
      onClick={() => {
        openBookingDetailPage();
      }}
    >
      <div className={classes.nameReceipt}>
        {renderName()}
        {renderReceipt()}
      </div>
      {renderAmount()}
    </div>
  );
}

PaymentItem.prototype = {
  payment: PropTypes.object.isRequired,
};

export default PaymentItem;
